import { useState, useCallback, createContext, useContext, ReactNode } from 'react';
import { View, Text, Pressable, Modal } from 'react-native';
import Animated, { FadeIn, FadeOut, ZoomIn, ZoomOut } from 'react-native-reanimated';
import { AlertCircle, CheckCircle, AlertTriangle, Info, X } from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';
import AnimatedPressable from '@/components/AnimatedPressable';

// Alert types
export type AlertType = 'info' | 'success' | 'warning' | 'error' | 'confirm';

export interface AlertButton {
  text: string;
  style?: 'default' | 'cancel' | 'destructive';
  onPress?: () => void;
}

export interface AlertOptions {
  title: string;
  message?: string;
  type?: AlertType;
  buttons?: AlertButton[];
}

interface AlertState {
  visible: boolean;
  options: AlertOptions | null;
}

interface AlertContextType {
  show: (options: AlertOptions) => Promise<number>;
  hide: () => void;
}

const AlertContext = createContext<AlertContextType | null>(null);

// Icons for each alert type
const AlertIcons: Record<AlertType, typeof Info> = {
  info: Info,
  success: CheckCircle,
  warning: AlertTriangle,
  error: AlertCircle,
  confirm: AlertTriangle,
};

const getAlertColors = (colors: ReturnType<typeof useThemeColors>): Record<AlertType, { icon: string; bg: string }> => ({
  info: { icon: colors.primary[600], bg: colors.primary[50] },
  success: { icon: colors.traffic.green, bg: colors.traffic.greenLight },
  warning: { icon: colors.amber.main, bg: colors.amber.light },
  error: { icon: colors.traffic.red, bg: colors.red.light },
  confirm: { icon: colors.amber.main, bg: colors.amber.light },
});

/**
 * Alert Dialog Provider - Wrap your app with this to enable alerts
 */
export function AlertProvider({ children }: { children: ReactNode }) {
  const colors = useThemeColors();
  const AlertColors = getAlertColors(colors);
  const [state, setState] = useState<AlertState>({ visible: false, options: null });
  const [resolveRef, setResolveRef] = useState<((index: number) => void) | null>(null);

  const show = useCallback((options: AlertOptions): Promise<number> => {
    return new Promise((resolve) => {
      setResolveRef(() => resolve);
      setState({ visible: true, options });
    });
  }, []);

  const hide = useCallback(() => {
    setState({ visible: false, options: null });
  }, []);

  const handleButtonPress = (index: number, button?: AlertButton) => {
    button?.onPress?.();
    resolveRef?.(index);
    hide();
  };

  const { visible, options } = state;
  const alertType = options?.type ?? 'info';
  const Icon = AlertIcons[alertType];
  const colorScheme = AlertColors[alertType];
  const buttons = options?.buttons ?? [{ text: 'OK', style: 'default' }];

  return (
    <AlertContext.Provider value={{ show, hide }}>
      {children}
      <Modal
        visible={visible}
        transparent
        animationType="none"
        onRequestClose={hide}
        statusBarTranslucent
      >
        <Animated.View
          entering={FadeIn.duration(200)}
          exiting={FadeOut.duration(150)}
          style={{
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          }}
        >
          <Pressable
            className="absolute inset-0"
            onPress={() => {
              // Close on backdrop tap if there's a cancel button
              const cancelIndex = buttons.findIndex(b => b.style === 'cancel');
              if (cancelIndex >= 0) {
                handleButtonPress(cancelIndex, buttons[cancelIndex]);
              }
            }}
          />
          <Animated.View
            entering={ZoomIn.duration(200).springify()}
            exiting={ZoomOut.duration(150)}
            style={{
              backgroundColor: colors.surface.primary,
              width: isWeb ? 400 : '85%',
              maxWidth: 400,
              borderRadius: 16,
              overflow: 'hidden',
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 10 },
              shadowOpacity: 0.2,
              shadowRadius: 25,
              elevation: 10,
            }}
          >
            {/* Header with Icon */}
            <View style={{ padding: 24, paddingBottom: 16 }}>
              <View
                style={{
                  alignItems: 'center',
                  marginBottom: 12,
                }}
              >
                <View
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 24,
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: colorScheme.bg,
                  }}
                >
                  <Icon size={24} color={colorScheme.icon} />
                </View>
              </View>
              <Text
                style={{
                  color: colors.text.primary,
                  fontSize: 18,
                  fontWeight: '600',
                  textAlign: 'center',
                  marginBottom: 8,
                }}
              >
                {options?.title}
              </Text>
              {options?.message && (
                <Text
                  style={{
                    color: colors.text.secondary,
                    fontSize: 14,
                    lineHeight: 20,
                    textAlign: 'center',
                  }}
                >
                  {options.message}
                </Text>
              )}
            </View>

            {/* Buttons */}
            <View
              style={{
                flexDirection: 'row',
                borderTopWidth: 1,
                borderTopColor: colors.border.light,
                minHeight: 48,
              }}
            >
              {buttons.map((button, index) => {
                const isDestructive = button.style === 'destructive';
                const isCancel = button.style === 'cancel';
                const isLast = index === buttons.length - 1;

                return (
                  <Pressable
                    key={index}
                    onPress={() => handleButtonPress(index, button)}
                    style={{
                      flex: 1,
                      paddingVertical: 14,
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRightWidth: isLast ? 0 : 1,
                      borderRightColor: colors.border.light,
                      backgroundColor: isDestructive
                        ? colors.red.light
                        : 'transparent',
                    }}
                  >
                    <Text
                      style={{
                        fontWeight: '600',
                        fontSize: 16,
                        color: isDestructive
                          ? colors.traffic.red
                          : isCancel
                          ? colors.text.secondary
                          : colors.primary[600],
                      }}
                    >
                      {button.text}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </Animated.View>
        </Animated.View>
      </Modal>
    </AlertContext.Provider>
  );
}

/**
 * Hook to show alerts - use this instead of Alert.alert()
 */
export function useAlert() {
  const context = useContext(AlertContext);

  if (!context) {
    throw new Error('useAlert must be used within an AlertProvider');
  }

  const { show, hide } = context;

  // Convenience methods
  const alert = useCallback(
    (title: string, message?: string) =>
      show({ title, message, type: 'info', buttons: [{ text: 'OK' }] }),
    [show]
  );

  const confirm = useCallback(
    (title: string, message?: string): Promise<boolean> =>
      show({
        title,
        message,
        type: 'confirm',
        buttons: [
          { text: 'Cancel', style: 'cancel' },
          { text: 'Confirm', style: 'default' },
        ],
      }).then((index) => index === 1),
    [show]
  );

  const confirmDestructive = useCallback(
    (title: string, message?: string, confirmText = 'Delete'): Promise<boolean> =>
      show({
        title,
        message,
        type: 'error',
        buttons: [
          { text: 'Cancel', style: 'cancel' },
          { text: confirmText, style: 'destructive' },
        ],
      }).then((index) => index === 1),
    [show]
  );

  const success = useCallback(
    (title: string, message?: string) =>
      show({ title, message, type: 'success', buttons: [{ text: 'OK' }] }),
    [show]
  );

  const error = useCallback(
    (title: string, message?: string) =>
      show({ title, message, type: 'error', buttons: [{ text: 'OK' }] }),
    [show]
  );

  const warning = useCallback(
    (title: string, message?: string) =>
      show({ title, message, type: 'warning', buttons: [{ text: 'OK' }] }),
    [show]
  );

  return {
    show,
    hide,
    alert,
    confirm,
    confirmDestructive,
    success,
    error,
    warning,
  };
}
